// AGC: DroidFarm - Theme Context Provider
// AGC: Handles light/dark/system theme modes with localStorage persistence
// AGC: Also handles accent color selection (blue/green)

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'

// AGC: Theme type definitions
export type Theme = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

// AGC: Accent color type definitions
export type AccentColor = 'blue' | 'green'

// AGC: Accent color configurations - DroidFarm Brand Palette
export const ACCENT_COLORS: Record<AccentColor, {
  name: string
  primary: string
  gradientStart: string
  gradientMid: string
  gradientEnd: string
  lightBg: string
  darkText: string
}> = {
  blue: {
    name: 'Classic',
    primary: '#243b53',
    gradientStart: '#243b53',
    gradientMid: '#1e4976',
    gradientEnd: '#2563eb',
    lightBg: '#f0f4f8',
    darkText: '#334e68',
  },
  green: {
    // AGC: DroidFarm Brand - Solid green gradient for high contrast
    name: 'DroidFarm',
    primary: '#1a6b5a',           // AGC: Brand teal from logo
    gradientStart: '#0a3d33',    // AGC: Dark forest (top-left)
    gradientMid: '#0d4a3e',      // AGC: Deep forest teal (middle)
    gradientEnd: '#1a6b5a',      // AGC: Brand teal (bottom-right)
    lightBg: '#e8eaed',          // AGC: Light gray background
    darkText: '#0d4a3e',         // AGC: Deep teal for text
  },
}

// AGC: Context value interface
export interface ThemeContextValue {
  /** AGC: Current theme setting (light, dark, or system) */
  theme: Theme
  /** AGC: Resolved theme after applying system preference (light or dark) */
  resolvedTheme: ResolvedTheme
  /** AGC: Set the theme directly */
  setTheme: (theme: Theme) => void
  /** AGC: Toggle between light and dark modes */
  toggleTheme: () => void
  /** AGC: Check if dark mode is active */
  isDark: boolean
  /** AGC: Current accent color */
  accentColor: AccentColor
  /** AGC: Set the accent color */
  setAccentColor: (color: AccentColor) => void
  /** AGC: Get current accent color config */
  accentConfig: typeof ACCENT_COLORS[AccentColor]
}

// AGC: Storage keys for localStorage
const THEME_STORAGE_KEY = 'droidfarm-theme'
const ACCENT_STORAGE_KEY = 'droidfarm-accent-color'

// AGC: Create context with undefined default
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

// AGC: Get system preference from matchMedia
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// AGC: Get stored theme from localStorage
const getStoredTheme = (): Theme | null => {
  if (typeof window === 'undefined') return null
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
  } catch {
    // AGC: localStorage not available (SSR or private browsing)
  }
  return null
}

// AGC: Get stored accent color from localStorage
const getStoredAccentColor = (): AccentColor | null => {
  if (typeof window === 'undefined') return null
  try {
    const stored = localStorage.getItem(ACCENT_STORAGE_KEY)
    if (stored === 'blue' || stored === 'green') {
      return stored
    }
  } catch {
    // AGC: localStorage not available
  }
  return null
}

// AGC: Apply theme class to document
const applyThemeToDocument = (resolvedTheme: ResolvedTheme, accentColor: AccentColor): void => {
  if (typeof document === 'undefined') return
  
  const root = document.documentElement
  
  // AGC: Remove both theme classes first
  root.classList.remove('light', 'dark')
  
  // AGC: Add the resolved theme class
  root.classList.add(resolvedTheme)
  
  // AGC: Set accent color as data attribute for CSS selectors
  root.setAttribute('data-accent', accentColor)
  
  // AGC: Apply accent color CSS variables
  const config = ACCENT_COLORS[accentColor]
  root.style.setProperty('--accent-primary', config.primary)
  root.style.setProperty('--accent-gradient-start', config.gradientStart)
  root.style.setProperty('--accent-gradient-mid', config.gradientMid)
  root.style.setProperty('--accent-gradient-end', config.gradientEnd)
  root.style.setProperty('--accent-light-bg', config.lightBg)
  root.style.setProperty('--accent-dark-text', config.darkText)
  
  // AGC: Set the hero-gradient CSS variable for inline styles
  root.style.setProperty(
    '--hero-gradient',
    `linear-gradient(135deg, ${config.gradientStart} 0%, ${config.gradientMid} 50%, ${config.gradientEnd} 100%)`
  )
  
  // AGC: Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]')
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      'content',
      resolvedTheme === 'dark' ? '#0f172a' : config.primary
    )
  }
}

// AGC: Provider props interface
interface ThemeProviderProps {
  children: ReactNode
  /** AGC: Default theme if none is stored (default: 'light' — dark mode not yet fully debugged) */
  defaultTheme?: Theme
  /** AGC: Default accent color if none is stored (default: 'blue') */
  defaultAccentColor?: AccentColor
}

/**
 * AGC: ThemeProvider component
 * AGC: Provides theme context to the entire app with:
 * AGC: - Light, dark, and system theme modes
 * AGC: - Accent color selection (blue/green)
 * AGC: - localStorage persistence
 * AGC: - System preference detection and sync
 * AGC: - No flash of wrong theme on load
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light',
  defaultAccentColor = 'green', // AGC: Default to DroidFarm brand colors
}) => {
  // AGC: Initialize theme from storage or default
  const [theme, setThemeState] = useState<Theme>(() => {
    return getStoredTheme() ?? defaultTheme
  })

  // AGC: Initialize accent color from storage or default
  const [accentColor, setAccentColorState] = useState<AccentColor>(() => {
    return getStoredAccentColor() ?? defaultAccentColor
  })

  // AGC: Track system preference for 'system' mode
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme)

  // AGC: Calculate resolved theme
  const resolvedTheme: ResolvedTheme = useMemo(() => {
    return theme === 'system' ? systemTheme : theme
  }, [theme, systemTheme])

  // AGC: Get current accent config
  const accentConfig = useMemo(() => ACCENT_COLORS[accentColor], [accentColor])

  // AGC: Apply theme class and accent color whenever they change
  useEffect(() => {
    applyThemeToDocument(resolvedTheme, accentColor)
  }, [resolvedTheme, accentColor])

  // AGC: Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent): void => {
      setSystemTheme(e.matches ? 'dark' : 'light')
    }

    // AGC: Add listener with modern API fallback
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
    } else {
      // AGC: Fallback for older browsers
      mediaQuery.addListener(handleChange)
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange)
      } else {
        mediaQuery.removeListener(handleChange)
      }
    }
  }, [])

  // AGC: Set theme and persist to localStorage
  const setTheme = useCallback((newTheme: Theme): void => {
    setThemeState(newTheme)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme)
    } catch {
      // AGC: localStorage not available
    }
  }, [])

  // AGC: Set accent color and persist to localStorage
  const setAccentColor = useCallback((newColor: AccentColor): void => {
    setAccentColorState(newColor)
    try {
      localStorage.setItem(ACCENT_STORAGE_KEY, newColor)
    } catch {
      // AGC: localStorage not available
    }
  }, [])

  // AGC: Toggle between light and dark (skipping system)
  const toggleTheme = useCallback((): void => {
    const newTheme: Theme = resolvedTheme === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
  }, [resolvedTheme, setTheme])

  // AGC: Memoize context value
  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme,
      isDark: resolvedTheme === 'dark',
      accentColor,
      setAccentColor,
      accentConfig,
    }),
    [theme, resolvedTheme, setTheme, toggleTheme, accentColor, setAccentColor, accentConfig]
  )

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

/**
 * AGC: useTheme hook
 * AGC: Access theme context from any component
 * AGC: Throws error if used outside ThemeProvider
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('AGC: useTheme must be used within a ThemeProvider')
  }
  return context
}

// AGC: Export context for advanced use cases
export { ThemeContext }





